import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export type SaveMode = 'overwrite' | 'new';

interface SaveChartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultName?: string;
  isExistingChart?: boolean;
  onSave: (options: { mode: SaveMode; name: string }) => Promise<void>;
}

export function SaveChartDialog({
  open,
  onOpenChange,
  defaultName = '',
  isExistingChart = false,
  onSave,
}: SaveChartDialogProps) {
  const [chartName, setChartName] = useState(defaultName);
  const [isSaving, setIsSaving] = useState(false);
  // For existing charts: null = choosing, 'overwrite' or 'new' = chosen
  const [selectedMode, setSelectedMode] = useState<SaveMode | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setChartName(defaultName);
      setSelectedMode(null);
    }
  }, [open, defaultName]);

  const handleSave = async (mode: SaveMode, name?: string) => {
    const finalName = name ?? chartName;
    if (mode === 'new' && !finalName.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave({ mode, name: finalName.trim() });
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in the parent component
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && chartName.trim()) {
      e.preventDefault();
      handleSave('new', chartName);
    }
  };

  // For new charts, show simple name input
  if (!isExistingChart) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Chart</DialogTitle>
            <DialogDescription>
              Enter a name for this chart. It will be saved and available on the Analytics page.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Label htmlFor="chart-name">Chart Name</Label>
            <Input
              id="chart-name"
              value={chartName}
              onChange={(e) => setChartName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter chart name"
              autoFocus
              disabled={isSaving}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSave('new', chartName)}
              disabled={!chartName.trim() || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // For existing charts, show choice first
  if (selectedMode === null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Chart</DialogTitle>
            <DialogDescription>
              This chart already exists. Would you like to overwrite it or save as a new chart?
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => handleSave('overwrite', defaultName)}
              disabled={isSaving}
            >
              <div className="text-left">
                <div className="font-medium">Overwrite existing chart</div>
                <div className="text-sm text-muted-foreground">
                  Update "{defaultName}" with your changes
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => setSelectedMode('new')}
              disabled={isSaving}
            >
              <div className="text-left">
                <div className="font-medium">Save as new chart</div>
                <div className="text-sm text-muted-foreground">
                  Create a new chart with these settings
                </div>
              </div>
            </Button>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // User chose "Save as new" - show name input
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as New Chart</DialogTitle>
          <DialogDescription>
            Enter a name for your new chart. It will be saved and available on the Analytics page.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Label htmlFor="chart-name">Chart Name</Label>
          <Input
            id="chart-name"
            value={chartName}
            onChange={(e) => setChartName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter chart name"
            autoFocus
            disabled={isSaving}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setSelectedMode(null)}
            disabled={isSaving}
          >
            Back
          </Button>
          <Button
            onClick={() => handleSave('new', chartName)}
            disabled={!chartName.trim() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
